import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { Tooltip } from 'flowbite-react';
import {
  AreaChart as RechartsAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { orgSelector, userSelector, locationSelector } from 'redux/selectors';
import { checkOrgAdmin, checkPermission, getUserOrg } from 'utils/utils';
import { defaultpageCount, permissionKeys } from 'utils/constants';
import { getDashboardKpis, getLowHighStockFilter, getSalesByWeek } from 'api/dashboardApi';

const HeaderWithInfo = ({ label, tooltip }: { label: string; tooltip: string }) => (
  <span className="inline-flex items-center justify-end gap-1">
    <span>{label}</span>
    <Tooltip content={tooltip} placement="top">
      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-gray-200 text-[10px] font-semibold text-gray-700 cursor-help">
        ?
      </span>
    </Tooltip>
  </span>
);

const StockMetricPage = () => {
  const { userInfo } = useSelector(userSelector);
  const { organizationId } = useSelector(orgSelector);
  const { locationId } = useSelector(locationSelector);
  const { PLATFORM_ADMIN } = permissionKeys;

  const isAdmin = checkPermission(userInfo, 'Organization', [PLATFORM_ADMIN], organizationId);
  const isOrgAdmin = checkOrgAdmin(userInfo, organizationId, isAdmin);

  const scopedUserOrgId = useMemo(
    () => (isOrgAdmin === true ? null : getUserOrg(userInfo, organizationId)?.userOrganizationId),
    [isOrgAdmin, userInfo, organizationId],
  );

  const scopedRegionIds = useMemo(() => {
    if (isOrgAdmin) return '';
    const userOrg = userInfo?.organizations?.find((org: any) => org.id === organizationId);
    const regions = userOrg?.regions || [];
    return regions.map((region: any) => region.id).join(',');
  }, [isOrgAdmin, userInfo, organizationId]);

  const baseFilter = useMemo(
    () => ({
      page: 0,
      limit: defaultpageCount,
      organizationId,
      userOrgId: scopedUserOrgId,
      regionIds: scopedRegionIds,
      locationId: locationId || '',
      dateRangeDays: 28,
    }),
    [organizationId, scopedUserOrgId, scopedRegionIds, locationId],
  );

  const { data: kpiData } = useQuery(['stockMetricsKpis', baseFilter], () => getDashboardKpis(baseFilter), {
    enabled: !!organizationId,
    staleTime: 60 * 1000,
  });

  const { data: lowStockData } = useQuery(
    ['stockMetricsLow', baseFilter],
    () => getLowHighStockFilter({ ...baseFilter, stockType: 'LowStock', limit: 200 }),
    { enabled: !!organizationId, staleTime: 60 * 1000 },
  );

  const { data: highStockData } = useQuery(
    ['stockMetricsHigh', baseFilter],
    () => getLowHighStockFilter({ ...baseFilter, stockType: 'HighStock', limit: 200 }),
    { enabled: !!organizationId, staleTime: 60 * 1000 },
  );
  const { data: salesByWeekData } = useQuery(['stockMetricsSalesByWeek', baseFilter], () => getSalesByWeek(baseFilter), {
    enabled: !!organizationId,
    staleTime: 60 * 1000,
  });

  const metrics = kpiData?.data || {};
  const lowRows = lowStockData?.data?.data || [];
  const highRows = highStockData?.data?.data || [];

  const formatCurrency = (value?: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(Number(value || 0));

  const grossSalesValues: number[] = (salesByWeekData?.data?.grossSales || [0, 0, 0, 0]).map(
    (value: any) => Number(value) || 0
  );
  const grossProfitValues: number[] = (salesByWeekData?.data?.grossProfit || [0, 0, 0, 0]).map(
    (value: any) => Number(value) || 0
  );

  // Generate date ranges for the last 4 weeks (Monday-Sunday)
  const weekRanges = useMemo(() => {
    const ranges: { start: string; end: string; label: string }[] = [];
    const today = new Date();
    const currentMonday = new Date(today);
    currentMonday.setDate(today.getDate() - today.getDay() + 1); // Get Monday of current week

    // Generate ranges for weeks 0-3 (0 = 3 weeks ago, 3 = current week)
    for (let i = 0; i < 4; i++) {
      const weekStart = new Date(currentMonday);
      weekStart.setDate(currentMonday.getDate() - (3 - i) * 7);
      
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6); // Sunday
      
      const formatDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      ranges.push({
        start: formatDate(weekStart),
        end: formatDate(weekEnd),
        label: formatDate(weekStart),
      });
    }
    return ranges;
  }, []);

  const chartData = useMemo(
    () =>
      weekRanges.map((range, i) => ({
        week: range.label,
        weekRange: `${range.start} - ${range.end}`,
        'Gross Sales': grossSalesValues[i],
        'Gross Profit': grossProfitValues[i],
      })),
    [weekRanges, grossSalesValues, grossProfitValues]
  );

  return (
    <div className="bg-[#f6f7fb] min-h-full p-6">
      <section className="mb-6">
        <p className="text-xs text-gray-400 mb-2">Based on the last {Math.floor(baseFilter.dateRangeDays / 7)} weeks</p>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <p className="text-xs text-gray-500 mb-2">On-Hand Inventory Value</p>
            <p className="text-3xl font-semibold text-gray-700">{formatCurrency(metrics.costOfInventory)}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <p className="text-xs text-gray-500 mb-2">Gross Sales</p>
            <p className="text-3xl font-semibold text-gray-700">{formatCurrency(metrics.grossSales)}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <p className="text-xs text-gray-500 mb-2">COGS</p>
            <p className="text-3xl font-semibold text-gray-700">{formatCurrency(metrics.cogs)}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <p className="text-xs text-gray-500 mb-2">Gross Profit</p>
            <p className="text-3xl font-semibold text-gray-700">{formatCurrency(metrics.grossRevenue)}</p>
          </div>
        </div>
      </section>

      <section className="bg-white border border-[#D5D5D5] shadow-[0_4px_8px_rgba(191,191,191,0.25)] rounded-[40px] p-5 mb-6 w-full">
        <p className="text-xs text-gray-500 mb-4">Sales by Week</p>
        <div className="w-full" style={{ height: 288 }}>
          <ResponsiveContainer width="100%" height="100%">
            <RechartsAreaChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <defs>
                <linearGradient id="colorGrossSales" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="colorGrossProfit" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#a1a1aa" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#a1a1aa" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
              <XAxis
                dataKey="week"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#6b7280' }}
                padding={{ left: 20, right: 20 }}
              />
              <YAxis
                width={80}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#6b7280' }}
                tickFormatter={(v) => formatCurrency(v)}
              />
              <RechartsTooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div className="bg-white border border-gray-200 rounded-lg shadow-md p-3 text-sm">
                        <p className="font-semibold text-gray-700 mb-2">{data.weekRange}</p>
                        {payload.map((entry: any, index: number) => (
                          <div key={index} className="flex items-center gap-2 mb-1">
                            <span
                              className="inline-block w-2 h-2 rounded-full"
                              style={{ backgroundColor: entry.color }}
                            />
                            <span className="text-gray-600">{entry.name}:</span>
                            <span className="font-medium text-gray-800">{formatCurrency(entry.value)}</span>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend
                verticalAlign="top"
                align="right"
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: 12, paddingBottom: 8 }}
              />
              <Area
                type="monotone"
                dataKey="Gross Sales"
                stroke="#7c3aed"
                strokeWidth={2.5}
                fill="url(#colorGrossSales)"
                dot={false}
                activeDot={{ r: 5, fill: '#7c3aed', stroke: '#fff', strokeWidth: 2 }}
              />
              <Area
                type="monotone"
                dataKey="Gross Profit"
                stroke="#6b7280"
                strokeWidth={2.5}
                fill="url(#colorGrossProfit)"
                dot={false}
                activeDot={{ r: 5, fill: '#6b7280', stroke: '#fff', strokeWidth: 2 }}
              />
            </RechartsAreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="font-semibold text-gray-800">Low Stock</p>
          </div>
          <div className="max-h-[280px] overflow-y-auto rounded-lg">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 sticky top-0 bg-white z-10">
                <tr>
                  <th className="text-left py-2">Item Name</th>
                  <th className="text-right py-2">
                    <HeaderWithInfo label="QOH" tooltip="Quantity on Hand" />
                  </th>
                  <th className="text-right py-2">
                    <HeaderWithInfo label="QOO" tooltip="Quantity on Order" />
                  </th>
                  <th className="text-right py-2 pr-3">
                    <HeaderWithInfo label="MS" tooltip="Minimum Stock" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {lowRows.map((row: any, index: number) => (
                  <tr key={`low-${index}`} className="border-t border-gray-100">
                    <td className="py-2 text-gray-700">{row.itemName}</td>
                    <td className="py-2 text-right text-gray-700">{row.quantity ?? 0}</td>
                    <td className="py-2 text-right text-gray-700">{row.quantityOnOrder ?? 0}</td>
                    <td className="py-2 text-right text-gray-700 pr-3" title={row.minimumStock == null ? 'Minimum stock is not set for this item at this location' : ''}>
                      {row.minimumStock ?? '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <p className="font-semibold text-gray-800 mb-3">High Stock</p>
          <div className="max-h-[280px] overflow-y-auto rounded-lg">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 sticky top-0 bg-white z-10">
                <tr>
                  <th className="text-left py-2">Item Name</th>
                  <th className="text-right py-2">
                    <HeaderWithInfo label="QOH" tooltip="Quantity on Hand" />
                  </th>
                  <th className="text-right py-2">
                    <HeaderWithInfo label="QOO" tooltip="Quantity on Order" />
                  </th>
                  <th className="text-right py-2 pr-3">
                    <HeaderWithInfo label="MS" tooltip="Minimum Stock" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {highRows.map((row: any, index: number) => (
                  <tr key={`high-${index}`} className="border-t border-gray-100">
                    <td className="py-2 text-gray-700">{row.itemName}</td>
                    <td className="py-2 text-right text-gray-700">{row.quantity ?? 0}</td>
                    <td className="py-2 text-right text-gray-700">{row.quantityOnOrder ?? 0}</td>
                    <td className="py-2 text-right text-gray-700 pr-3" title={row.minimumStock == null ? 'Minimum stock is not set for this item at this location' : ''}>
                      {row.minimumStock ?? '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StockMetricPage;
